/* Small themed building blocks shared across screens. */
import { Component, Children, isValidElement, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode, ButtonHTMLAttributes, InputHTMLAttributes } from 'react';

export function Button({
  variant = 'primary', className = '', ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' }) {
  const base = variant === 'primary' ? 'btn-primary' : 'btn-ghost';
  return <button className={`${base} ${className}`} {...props} />;
}

export function Card({ className = '', tut, children }: { className?: string; tut?: string; children: ReactNode }) {
  return <div className={`card p-4 ${className}`} data-tut={tut}>{children}</div>;
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-dim">{hint}</span>}
    </label>
  );
}

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  // Merge with the base class so a passed className augments (not replaces) it.
  return <input className={`input ${className}`} {...props} />;
}

/* Select - a fully custom, theme-aware dropdown that replaces the native
 * <select>. The native option list is drawn by the OS (often a white popup
 * with near-invisible text on a dark theme) and cannot be styled reliably;
 * this version owns its markup so the open list matches the app and stays
 * legible. Drop-in compatible with the old API: pass option children and an
 * onChange that reads e.target.value. Full mouse + keyboard support
 * (Arrows, Home/End, Enter/Space, Esc, type-ahead). Pass size="sm" for a
 * compact trigger (e.g. inside HUDs / dense toolbars). */
type Opt = { value: string; label: ReactNode; text: string; disabled?: boolean };

type SelectProps = {
  value?: string;
  onChange?: (e: { target: { value: string } }) => void;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  name?: string;
  'aria-label'?: string;
};

// Flatten an option's children into a plain string for type-ahead + a11y.
function nodeText(node: ReactNode): string {
  if (node == null || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (isValidElement(node)) return nodeText((node.props as { children?: ReactNode }).children);
  return '';
}

export function Select({ className = '', children, value, onChange, disabled, size = 'md', name, ...rest }: SelectProps) {
  const opts: Opt[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const p = child.props as { value?: string | number; children?: ReactNode; disabled?: boolean };
    const v = String(p.value ?? '');
    opts.push({ value: v, label: p.children, text: nodeText(p.children).trim(), disabled: p.disabled });
  });

  const sm = size === 'sm';
  const selected = opts.find((o) => o.value === value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0); // highlighted index while open
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeBuf = useRef({ str: '', t: 0 });
  const listId = useId();

  const choose = (i: number) => {
    const o = opts[i];
    if (!o || o.disabled) return;
    if (o.value !== value) onChange?.({ target: { value: o.value } });
    setOpen(false);
  };

  const openList = () => {
    if (disabled) return;
    const cur = opts.findIndex((o) => o.value === value);
    setActive(cur >= 0 ? cur : opts.findIndex((o) => !o.disabled));
    setOpen(true);
  };

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[active] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const step = (dir: 1 | -1) => {
    setActive((i) => {
      let n = i;
      for (let k = 0; k < opts.length; k++) {
        n = (n + dir + opts.length) % opts.length;
        if (!opts[n]?.disabled) return n;
      }
      return i;
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) { e.preventDefault(); openList(); }
      return;
    }
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); step(1); break;
      case 'ArrowUp': e.preventDefault(); step(-1); break;
      case 'Home': e.preventDefault(); setActive(opts.findIndex((o) => !o.disabled)); break;
      case 'End': {
        e.preventDefault();
        for (let i = opts.length - 1; i >= 0; i--) { if (!opts[i]?.disabled) { setActive(i); break; } }
        break;
      }
      case 'Enter': case ' ': e.preventDefault(); choose(active); break;
      case 'Escape': e.preventDefault(); setOpen(false); break;
      case 'Tab': setOpen(false); break;
      default:
        if (e.key.length === 1) {
          const now = Date.now();
          typeBuf.current.str = now - typeBuf.current.t > 600 ? e.key : typeBuf.current.str + e.key;
          typeBuf.current.t = now;
          const q = typeBuf.current.str.toLowerCase();
          const hit = opts.findIndex((o) => !o.disabled && o.text.toLowerCase().startsWith(q));
          if (hit >= 0) setActive(hit);
        }
    }
  };

  const ic = sm ? 14 : 16;

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={rest['aria-label']}
        data-name={name}
        className={`input flex w-full items-center justify-between gap-2 text-left ${sm ? '!rounded-[9px] !py-1.5 !pl-2.5 !pr-2 !text-xs' : 'pr-3'} ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      >
        <span className={`truncate ${selected ? 'text-txt' : 'text-dim'}`}>
          {selected ? selected.label : 'Select…'}
        </span>
        <svg
          width={ic} height={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
          className={`shrink-0 text-mut transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          // When the Select sits inside a <label> (e.g. a Field), a click on an
          // option would also activate the labeled control — the trigger button —
          // and instantly reopen the list. Cancelling the default action stops
          // the label forwarding; the option's own onClick has already run.
          onClick={(e) => e.preventDefault()}
          className={`absolute z-50 mt-2 max-h-64 min-w-full origin-top overflow-auto whitespace-nowrap rounded-[12px] border border-line bg-[color:var(--raised)] ${sm ? 'p-1' : 'p-1.5'} shadow-elev-lg ring-1 ring-line animate-[selectIn_.13s_ease-out]`}
        >
          {opts.length === 0 && <li className="px-3 py-2 text-sm text-dim">No options</li>}
          {opts.map((o, i) => {
            const isSel = o.value === value;
            const isActive = i === active;
            return (
              <li
                key={o.value || i}
                role="option"
                aria-selected={isSel}
                aria-disabled={o.disabled}
                onMouseEnter={() => !o.disabled && setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(i)}
                className={[
                  'flex items-center justify-between gap-2 rounded-[9px] transition-colors',
                  sm ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm',
                  o.disabled ? 'cursor-not-allowed text-dim' : 'cursor-pointer',
                  isActive && !o.disabled ? 'bg-acc/15 text-txt' : 'text-mut',
                  isSel && !isActive ? 'text-txt' : '',
                ].join(' ')}
              >
                <span className="truncate">{o.label}</span>
                {isSel && (
                  <svg
                    width={ic} height={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
                    className="shrink-0 text-acc" aria-hidden="true"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function Stat({ label, value, accent, tut }: { label: string; value: ReactNode; accent?: string; tut?: string }) {
  return (
    <div className="card px-4 py-3" data-tut={tut}>
      <div className="text-[11px] uppercase tracking-wide text-mut">{label}</div>
      <div className={`mt-0.5 text-xl font-bold ${accent ?? 'text-txt'}`}>{value}</div>
    </div>
  );
}

export function Badge({ tone = 'mut', children }: { tone?: 'good' | 'warn' | 'bad' | 'acc' | 'mut'; children: ReactNode }) {
  const map: Record<string, string> = {
    good: 'bg-good/15 text-good',
    warn: 'bg-warn/15 text-warn',
    bad: 'bg-bad/15 text-bad',
    acc: 'bg-acc/15 text-acc',
    mut: 'bg-panel2 text-mut',
  };
  return <span className={`chip ${map[tone]}`}>{children}</span>;
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-mut">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-card-b border-t-acc" />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return children ? (
    <div className="rounded-sm border border-bad/30 bg-bad/10 px-3 py-2 text-sm text-bad">{children}</div>
  ) : null;
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return <div className="rounded-sm border border-dashed border-card-b px-4 py-6 text-center text-sm text-mut">{children}</div>;
}

export class ErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}
